import { useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";

type VoiceWaveformProps = {
  getAudioLevel: () => number;
};

const SAMPLE_INTERVAL_MS = 50;
const BAR_WIDTH = 2;
const BAR_GAP = 2;
const MIN_BAR_HEIGHT = 2;

export default function VoiceWaveform({ getAudioLevel }: VoiceWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const theme = useTheme();
  const barColor = theme.palette.text.secondary;

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) {
      return;
    }

    const levels: number[] = [];
    let animationFrame = 0;
    let lastSampleTime = 0;

    const draw = (time: number) => {
      if (time - lastSampleTime >= SAMPLE_INTERVAL_MS) {
        lastSampleTime = time;
        levels.push(Math.min(1, getAudioLevel() * 4));
      }

      const pixelRatio = globalThis.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      const canvasWidth = Math.round(width * pixelRatio);
      const canvasHeight = Math.round(height * pixelRatio);
      if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
      }

      const barWidth = BAR_WIDTH * pixelRatio;
      const barGap = BAR_GAP * pixelRatio;
      const maxBars = Math.max(1, Math.floor(canvasWidth / (barWidth + barGap)));
      while (levels.length > maxBars) {
        levels.shift();
      }

      context.clearRect(0, 0, canvasWidth, canvasHeight);
      context.fillStyle = barColor;
      levels.forEach((level, index) => {
        const x = canvasWidth - (levels.length - index) * (barWidth + barGap);
        const barHeight = Math.max(MIN_BAR_HEIGHT * pixelRatio, level * canvasHeight);
        const y = (canvasHeight - barHeight) / 2;
        context.beginPath();
        context.roundRect(x, y, barWidth, barHeight, barWidth / 2);
        context.fill();
      });

      animationFrame = requestAnimationFrame(draw);
    };

    animationFrame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationFrame);
  }, [barColor, getAudioLevel]);

  return (
    <Box
      component={"canvas"}
      ref={canvasRef}
      aria-hidden={true}
      sx={{ display: "block", height: "2rem", width: "100%" }}
    />
  );
}
